import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Plot from 'react-plotly.js';
import WorkflowSidebar from './components/WorkflowSidebar';
import MethodCard from './components/MethodCard';
import CancelButton from './components/CancelButton';
import apiClient from '../../api/client';
import SaveResultsWizard from '../../components/chemistry/SaveResultsWizard';
import { useWorkbenchContext } from '../../hooks/useWorkbenchContext';
import WorkbenchContextBar from '../../components/workbench/WorkbenchContextBar';
import ToolSwitcher from '../../components/workbench/ToolSwitcher';
import { useToolStep } from '../../hooks/useWorkbenchContext';

const STEPS = [
  { name: 'Upload' },
  { name: 'Map' },
  { name: 'Fit' },
  { name: 'Review' },
  { name: 'Export' },
];

const SAMPLE_DATA = `0.5,0.12
1,0.22
2,0.38
5,0.62
10,0.79
20,0.91
50,0.99
100,1.04
200,1.08`;

type FitType = 'mm' | 'scatchard' | 'rosenthal' | 'klotz';

const WorkbenchKinetics: React.FC = () => {
  const { smiles: ctxSmiles, moleculeId: ctxMoleculeId, fileId: ctxFileId, projectId: ctxProjectId } = useWorkbenchContext();
  const [step, setStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [rawText, setRawText] = useState('');
  const [parsedPoints, setParsedPoints] = useState<{ s: number; v: number }[]>([]);
  const [parseError, setParseError] = useState('');
  const [activeFit, setActiveFit] = useState<FitType>('mm');
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [secondaryResults, setSecondaryResults] = useState<Record<string, any>>({});
  const [error, setError] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const { bailOutText, setHasSaved } = useToolStep();

  const parseData = (text: string) => {
    setParseError('');
    const lines = text.trim().split('\n').filter((l) => l.trim());
    const points: { s: number; v: number }[] = [];
    for (let idx = 0; idx < lines.length; idx++) {
      const parts = lines[idx].split(/[,\t;]+/).map((s) => s.trim());
      if (parts.length < 2) { if (idx === 0) continue; setParseError(`Line ${idx + 1}: need 2 columns`); return; }
      const s = parseFloat(parts[0]);
      const v = parseFloat(parts[1]);
      if (isNaN(s) || isNaN(v)) { if (idx === 0) continue; setParseError(`Line ${idx + 1}: non-numeric`); return; }
      points.push({ s, v });
    }
    if (points.length < 4) { setParseError('Need at least 4 data points'); return; }
    setParsedPoints(points);
    setCompletedSteps((prev) => Array.from(new Set([...prev, 0])));
    setStep(1);
  };

  const runFit = async (fitType: FitType = 'mm') => {
    setIsRunning(true);
    setError('');
    if (fitType === 'mm') setResult(null);
    setActiveFit(fitType);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const url = `/v2/kinetics/fit/${fitType}`;
      let body: any;

      if (fitType === 'mm') {
        body = { points: parsedPoints };
      } else {
        // Scatchard/Rosenthal/Klotz need {b, f} format
        body = { points: parsedPoints.map((p) => ({ b: p.v, f: p.s })) };
      }

      const resp = await apiClient.post(url, body, { signal: controller.signal });
      const data = resp.data;

      if (fitType === 'mm') {
        setResult(data);
        setCompletedSteps((prev) => Array.from(new Set([...prev, 2])));
        setStep(3);
      } else {
        setSecondaryResults((prev) => ({ ...prev, [fitType]: data }));
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        setError('Fit cancelled');
      } else {
        setError(err.message || 'Fit failed');
      }
    } finally {
      setIsRunning(false);
      abortRef.current = null;
    }
  };

  const handleCancel = () => { abortRef.current?.abort(); };

  const copyResults = () => {
    if (!result) return;
    const lines: string[] = [];
    if (result.params) {
      Object.entries(result.params as Record<string, number>).forEach(([k, v]) =>
        lines.push(`${k}: ${(v as number).toPrecision(6)}`)
      );
    }
    if (result.r2 != null) lines.push(`R2: ${(result.r2 as number).toFixed(6)}`);
    // Include secondary transform params
    Object.entries(secondaryResults).forEach(([fitName, data]: [string, any]) => {
      lines.push(`\n--- ${fitName} ---`);
      if (data.params) {
        Object.entries(data.params as Record<string, number>).forEach(([k, v]) =>
          lines.push(`${k}: ${(v as number).toPrecision(6)}`)
        );
      }
      if (data.r2 != null) lines.push(`R2: ${(data.r2 as number).toFixed(6)}`);
    });
    navigator.clipboard.writeText(lines.join('\n'));
  };

  const downloadCSV = () => {
    if (!result) return;
    // Use consistent 3-column format throughout so the CSV reimports cleanly
    const lines = ['parameter,value,'];
    if (result.params) {
      Object.entries(result.params as Record<string, number>).forEach(([k, v]) => {
        lines.push(`${k},${v},`);
      });
    }
    if (result.r2 != null) lines.push(`R2,${result.r2},`);
    if (result.data) {
      lines.push(',,');
      lines.push('[S],observed,residual');
      const resMap = new Map<number, number>();
      if (result.residuals) {
        (result.residuals.x as number[]).forEach((x: number, i: number) => resMap.set(x, (result.residuals.y as number[])[i]));
      }
      (result.data.s as number[]).forEach((s: number, i: number) => {
        lines.push(`${s},${(result.data.v as number[])[i]},${resMap.get(s) ?? ''}`);
      });
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'kinetics_results.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <WorkbenchContextBar bailOutText={bailOutText} />
      <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
      <WorkflowSidebar steps={STEPS} currentStep={step} completedSteps={completedSteps} onStepClick={setStep} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={statusBarStyle}>
          <Link to="/hubs" style={{ color: '#f59e0b', textDecoration: 'none' }}>Toolbox</Link>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <ToolSwitcher currentToolId="kinetics" />
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Engine: SciPy</span>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Step {step + 1}/{STEPS.length}</span>
          <div style={{ flex: 1 }} />
          <CancelButton onCancel={handleCancel} isRunning={isRunning} label="Stop Fit" />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', gap: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {step === 0 && (
              <div>
                <h2 style={h2Style}>Paste substrate-velocity data</h2>
                <p style={descStyle}>CSV format: [S] (substrate concentration), v (velocity)</p>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <button onClick={() => setRawText(SAMPLE_DATA)} style={btnStyle}>Load Sample Data</button>
                  <label style={{ ...btnStyle, cursor: 'pointer' }}>
                    Upload File
                    <input type="file" accept=".csv,.txt,.tsv" style={{ display: 'none' }}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) f.text().then(setRawText); }} />
                  </label>
                </div>
                <textarea value={rawText} onChange={(e) => setRawText(e.target.value)}
                  placeholder="[S],v&#10;0.5,0.12&#10;1,0.22&#10;5,0.62&#10;20,0.91" style={textareaStyle} rows={12} />
                {parseError && <div style={errorStyle}>{parseError}</div>}
                <button onClick={() => parseData(rawText)} disabled={!rawText.trim()}
                  style={{ ...primaryBtnStyle, marginTop: 12, opacity: rawText.trim() ? 1 : 0.5 }}>Parse Data</button>
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 style={h2Style}>Data Preview ({parsedPoints.length} points)</h2>
                <div style={tableContainerStyle}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                        <th style={thStyle}>#</th><th style={thStyle}>[S]</th><th style={thStyle}>v</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedPoints.map((p, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid var(--card-bg)' }}>
                          <td style={tdStyle}>{idx + 1}</td><td style={tdStyle}>{p.s}</td><td style={tdStyle}>{p.v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button onClick={() => { setCompletedSteps((prev) => Array.from(new Set([...prev, 1]))); setStep(2); }} style={{ ...primaryBtnStyle, marginTop: 12 }}>
                  Confirm &amp; Continue
                </button>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 style={h2Style}>Run Enzyme Kinetics Fit</h2>
                <p style={descStyle}>Primary: Michaelis-Menten. Optional linear transforms below.</p>
                {!isRunning && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={() => runFit('mm')} style={primaryBtnStyle}>Fit Michaelis-Menten</button>
                    <button onClick={() => runFit('scatchard')} style={btnStyle}>Scatchard</button>
                    <button onClick={() => runFit('rosenthal')} style={btnStyle}>Rosenthal</button>
                    <button onClick={() => runFit('klotz')} style={btnStyle}>Klotz</button>
                  </div>
                )}
                {isRunning && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
                    <div style={spinnerStyle} />
                    <span style={{ color: '#f59e0b', fontSize: 14 }}>Running {activeFit.toUpperCase()} fit...</span>
                  </div>
                )}
                {error && <div style={errorStyle}>{error}</div>}
              </div>
            )}

            {step === 3 && result && (
              <div>
                <h2 style={h2Style}>Michaelis-Menten Results</h2>

                {/* Parameter cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 20 }}>
                  {result.params && Object.entries(result.params as Record<string, number>).map(([key, value]) => (
                    <div key={key} style={resultCardStyle}>
                      <div style={resultLabelStyle}>{key}</div>
                      <div style={resultValueStyle}>{(value as number).toPrecision(4)}</div>
                    </div>
                  ))}
                  {result.r2 != null && (
                    <div style={resultCardStyle}>
                      <div style={resultLabelStyle}>R²</div>
                      <div style={resultValueStyle}>{(result.r2 as number).toFixed(6)}</div>
                    </div>
                  )}
                </div>

                {/* Michaelis-Menten curve: observed + fitted */}
                {result.data && result.fit && (
                  <div style={chartContainerStyle}>
                    <Plot
                      data={[
                        {
                          x: result.data.s,
                          y: result.data.v,
                          type: 'scatter' as const,
                          mode: 'markers' as const,
                          name: 'Observed',
                          marker: { color: '#f59e0b', size: 8 },
                        },
                        {
                          x: result.fit.s,
                          y: result.fit.v,
                          type: 'scatter' as const,
                          mode: 'lines' as const,
                          name: 'Fitted (MM)',
                          line: { color: '#3b82f6', width: 2 },
                        },
                      ]}
                      layout={{
                        title: { text: 'Michaelis-Menten Curve', font: { color: '#f5f5f5', size: 14 } },
                        xaxis: { title: { text: '[S] (substrate concentration)', font: { color: '#a7abb6' } }, gridcolor: 'rgba(255,255,255,0.06)', tickfont: { color: '#a7abb6' } },
                        yaxis: { title: { text: 'v (velocity)', font: { color: '#a7abb6' } }, gridcolor: 'rgba(255,255,255,0.06)', tickfont: { color: '#a7abb6' } },
                        paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
                        legend: { font: { color: '#a7abb6' }, x: 0.7, y: 0.95 },
                        margin: { t: 40, r: 20, b: 50, l: 60 }, height: 360,
                      }}
                      config={{ displayModeBar: false, responsive: true }}
                      style={{ width: '100%' }}
                    />
                  </div>
                )}

                {/* Residuals chart */}
                {result.residuals && (
                  <div style={chartContainerStyle}>
                    <Plot
                      data={[
                        {
                          x: result.residuals.x,
                          y: result.residuals.y,
                          type: 'bar' as const,
                          name: 'Residuals',
                          marker: { color: (result.residuals.y as number[]).map((v: number) => v >= 0 ? '#22c55e' : '#ef4444') },
                        },
                      ]}
                      layout={{
                        title: { text: 'Residuals', font: { color: '#f5f5f5', size: 14 } },
                        xaxis: { title: { text: '[S]', font: { color: '#a7abb6' } }, gridcolor: 'rgba(255,255,255,0.06)', tickfont: { color: '#a7abb6' } },
                        yaxis: { title: { text: 'Residual', font: { color: '#a7abb6' } }, gridcolor: 'rgba(255,255,255,0.06)', tickfont: { color: '#a7abb6' }, zeroline: true, zerolinecolor: 'rgba(255,255,255,0.2)' },
                        paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
                        margin: { t: 40, r: 20, b: 50, l: 60 }, height: 240, showlegend: false,
                      }}
                      config={{ displayModeBar: false, responsive: true }}
                      style={{ width: '100%' }}
                    />
                  </div>
                )}

                {/* Built-in transforms from MM response */}
                {result.transforms && (
                  <div style={{ marginTop: 16 }}>
                    <h3 style={{ color: '#a7abb6', fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Linear Transforms</h3>
                    {Object.entries(result.transforms as Record<string, { x: number[]; y: number[] }>).map(([name, tData]) => {
                      const labels: Record<string, { x: string; y: string; title: string }> = {
                        lineweaver_burk: { x: '1/[S]', y: '1/v', title: 'Lineweaver-Burk' },
                        eadie_hofstee: { x: 'v/[S]', y: 'v', title: 'Eadie-Hofstee' },
                        hanes_woolf: { x: '[S]', y: '[S]/v', title: 'Hanes-Woolf' },
                        hill: { x: 'log [S]', y: 'log(v/(Vmax-v))', title: 'Hill Plot' },
                      };
                      const lbl = labels[name] || { x: 'x', y: 'y', title: name.replace(/_/g, ' ') };
                      return (
                        <details key={name} style={{ marginBottom: 8 }}>
                          <summary style={{ color: '#f59e0b', cursor: 'pointer', fontSize: 13 }}>{lbl.title}</summary>
                          <div style={{ ...chartContainerStyle, marginTop: 8 }}>
                            <Plot
                              data={[{
                                x: tData.x, y: tData.y,
                                type: 'scatter' as const, mode: 'markers+lines' as const,
                                marker: { color: '#f59e0b', size: 6 }, line: { color: '#3b82f6', width: 1.5 },
                              }]}
                              layout={{
                                title: { text: lbl.title, font: { color: '#f5f5f5', size: 13 } },
                                xaxis: { title: { text: lbl.x, font: { color: '#a7abb6' } }, gridcolor: 'rgba(255,255,255,0.06)', tickfont: { color: '#a7abb6' } },
                                yaxis: { title: { text: lbl.y, font: { color: '#a7abb6' } }, gridcolor: 'rgba(255,255,255,0.06)', tickfont: { color: '#a7abb6' } },
                                paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
                                margin: { t: 36, r: 16, b: 44, l: 56 }, height: 260, showlegend: false,
                              }}
                              config={{ displayModeBar: false, responsive: true }}
                              style={{ width: '100%' }}
                            />
                          </div>
                        </details>
                      );
                    })}
                  </div>
                )}

                {/* Secondary transforms (Scatchard, etc.) */}
                {Object.keys(secondaryResults).length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <h3 style={{ color: '#a7abb6', fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Additional Fits</h3>
                    {Object.entries(secondaryResults).map(([fitName, data]: [string, any]) => (
                      <details key={fitName} style={{ marginBottom: 8 }}>
                        <summary style={{ color: '#f59e0b', cursor: 'pointer', fontSize: 13, textTransform: 'capitalize' }}>{fitName}</summary>
                        <div style={{ marginTop: 8 }}>
                          {data.params && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8, marginBottom: 8 }}>
                              {Object.entries(data.params as Record<string, number>).map(([k, v]) => (
                                <div key={k} style={resultCardStyle}>
                                  <div style={resultLabelStyle}>{k}</div>
                                  <div style={{ ...resultValueStyle, fontSize: 15 }}>{(v as number).toPrecision(4)}</div>
                                </div>
                              ))}
                              {data.r2 != null && (
                                <div style={resultCardStyle}>
                                  <div style={resultLabelStyle}>R²</div>
                                  <div style={{ ...resultValueStyle, fontSize: 15 }}>{(data.r2 as number).toFixed(6)}</div>
                                </div>
                              )}
                            </div>
                          )}
                          {data.data && data.fit && (
                            <div style={chartContainerStyle}>
                              <Plot
                                data={[
                                  { x: data.data.x || data.data.s, y: data.data.y || data.data.v, type: 'scatter' as const, mode: 'markers' as const, name: 'Data', marker: { color: '#f59e0b', size: 6 } },
                                  { x: data.fit.x || data.fit.s, y: data.fit.y || data.fit.v, type: 'scatter' as const, mode: 'lines' as const, name: 'Fit', line: { color: '#3b82f6', width: 2 } },
                                ]}
                                layout={{
                                  paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
                                  xaxis: { gridcolor: 'rgba(255,255,255,0.06)', tickfont: { color: '#a7abb6' } },
                                  yaxis: { gridcolor: 'rgba(255,255,255,0.06)', tickfont: { color: '#a7abb6' } },
                                  legend: { font: { color: '#a7abb6' } },
                                  margin: { t: 20, r: 16, b: 40, l: 56 }, height: 260,
                                }}
                                config={{ displayModeBar: false, responsive: true }}
                                style={{ width: '100%' }}
                              />
                            </div>
                          )}
                        </div>
                      </details>
                    ))}
                  </div>
                )}

                {/* Run secondary fits from results page */}
                {!isRunning && (
                  <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {!secondaryResults.scatchard && <button onClick={() => runFit('scatchard')} style={btnStyle}>+ Scatchard</button>}
                    {!secondaryResults.rosenthal && <button onClick={() => runFit('rosenthal')} style={btnStyle}>+ Rosenthal</button>}
                    {!secondaryResults.klotz && <button onClick={() => runFit('klotz')} style={btnStyle}>+ Klotz</button>}
                  </div>
                )}

                <button onClick={() => { setCompletedSteps((prev) => Array.from(new Set([...prev, 3]))); setStep(4); }} style={{ ...primaryBtnStyle, marginTop: 16 }}>
                  Continue to Export
                </button>
              </div>
            )}

            {step === 4 && (
              <div>
                <h2 style={h2Style}>Export Results</h2>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={copyResults} style={btnStyle}>Copy Results</button>
                  <button onClick={downloadCSV} style={primaryBtnStyle}>Download CSV</button>
                  <button onClick={() => setSaveOpen(true)} style={btnStyle}>Save to Project</button>
                </div>
                <SaveResultsWizard isOpen={saveOpen} onClose={() => setSaveOpen(false)} rawData={result} toolName="Kinetics" toolKey="kinetics" smiles={ctxSmiles} moleculeId={ctxMoleculeId} fileId={ctxFileId} projectId={ctxProjectId} />
              </div>
            )}
          </div>

          <div style={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <MethodCard engine="SciPy" title="Michaelis-Menten"
              description="Non-linear regression of substrate-velocity data to estimate Km and Vmax."
              equation="v = (Vmax × [S]) / (Km + [S])"
              parameters={[
                { name: 'Vmax', desc: 'Maximum reaction velocity' },
                { name: 'Km', desc: 'Michaelis constant (half-Vmax [S])' },
              ]}
              reference="Michaelis & Menten (1913) Biochem Z 49:333-369"
              source="scipy.optimize.curve_fit" />
            <MethodCard engine="SciPy" title="Linear Transforms"
              description="Lineweaver-Burk (1/v vs 1/[S]), Eadie-Hofstee (v vs v/[S]), Hanes-Woolf ([S]/v vs [S]). Visual verification only — primary fit uses non-linear regression."
              reference="Lineweaver & Burk (1934) JACS 56:658-666" source="Linear regression" />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

const statusBarStyle: React.CSSProperties = {
  padding: '8px 20px', background: 'var(--hover-bg)', borderBottom: '1px solid var(--card-border)',
  fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8,
};
const h2Style: React.CSSProperties = { color: '#f5f5f5', fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 0 };
const descStyle: React.CSSProperties = { color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 };
const btnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500,
};
const primaryBtnStyle: React.CSSProperties = { ...btnStyle, background: '#f59e0b', color: 'var(--content-bg)', border: '1px solid #f59e0b', fontWeight: 600 };
const textareaStyle: React.CSSProperties = {
  width: '100%', padding: 14, background: 'var(--card-bg)', border: '1px solid var(--card-border)',
  borderRadius: 10, color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace', resize: 'vertical', outline: 'none', boxSizing: 'border-box',
};
const thStyle: React.CSSProperties = { padding: '8px 12px', textAlign: 'left', color: '#a7abb6', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdStyle: React.CSSProperties = { padding: '6px 12px', color: 'var(--text-primary)', fontFamily: 'monospace', fontSize: 13 };
const errorStyle: React.CSSProperties = { marginTop: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fb7185', fontSize: 13 };
const spinnerStyle: React.CSSProperties = { width: 20, height: 20, border: '3px solid var(--card-border)', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' };
const tableContainerStyle: React.CSSProperties = { background: 'var(--card-bg)', borderRadius: 10, border: '1px solid var(--card-border)', overflow: 'hidden', marginBottom: 16 };
const resultCardStyle: React.CSSProperties = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '12px 14px' };
const resultLabelStyle: React.CSSProperties = { color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 };
const resultValueStyle: React.CSSProperties = { color: '#f59e0b', fontSize: 18, fontWeight: 700, fontFamily: 'monospace' };
const chartContainerStyle: React.CSSProperties = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: 16, marginBottom: 16 };

export default WorkbenchKinetics;
